import { TIME_RANGE } from '../constants';
import { DateInfo } from 'data';
import dayjs from 'dayjs';
import 'dayjs/locale/de'; // example for German
import moment from 'moment';
import utc from 'dayjs/plugin/utc';
import localeData from 'dayjs/plugin/localeData';

export interface CalendarData {
  daysOfWeek: string[];
  monthLabel: string;
  monthDates: number[];
  today: number;
}

const getAbbreviatedMonth = (dateString: string): string => {
  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0'); // Ensure day is 2 digits
  const month = date.toLocaleString('en-GB', { month: 'short' }); // Get abbreviated month name (e.g., "May")

  return `${day} ${month}`;
};

const convertToAmPm = (selectedDate: string, selectedTime: string): string => {
  // Combine date and time into a single string
  const combinedDateTime = `${selectedDate}T${selectedTime}:00`; // Combine date and time
  // Create a Date object from the combined string (this will interpret it in UTC)
  const date = new Date(combinedDateTime);

  // Format the time in UTC to 12-hour AM/PM format
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true, // 12-hour format with AM/PM
    timeZone: 'UTC' // Ensure it's using UTC time zone
  });
  return formattedTime;
};

dayjs.extend(utc);
dayjs.extend(localeData);

const getFormattedTime = (value?: string, language = 'en') => {
  if (!value) return '';

  const formatted = value.substring(0, 5);

  return language === 'de' ? `${formatted} Uhr` : formatted;
};
export const generateCalendarData = (date = dayjs(), savedLang: string): CalendarData => {
  // Set locale before formatting
  dayjs.locale(savedLang);

  // Localized day names (short) according to locale
  // obtain localized short weekday names from localeData plugin
  // then rotate so week starts on Monday (index 1)
  const localizedDaysRaw = dayjs().localeData().weekdaysShort();
  const localizedDays = [...localizedDaysRaw.slice(1), localizedDaysRaw[0]];

  const monthLabel = date.format(dateFormat.MMM_YYYY);

  const daysInMonth = date.daysInMonth();

  // Calculate offset assuming week starts on Monday
  let firstDayOfMonth = dayjs(`${date.year()}-${date.month() + 1}-01`).day(); // 0 = Sunday
  firstDayOfMonth = ((firstDayOfMonth + 6) % 7) as 0 | 1 | 2 | 3 | 4 | 5 | 6;

  const leadingEmptySlots = new Array(firstDayOfMonth).fill(null);
  const monthDates = [
    ...leadingEmptySlots,
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1)
  ];

  const today = date.date();

  return {
    daysOfWeek: localizedDays,
    monthLabel,
    monthDates,
    today
  };
};

// date formats
const dateFormat = {
  HH_MM_A: 'hh:mm A',
  DD_MM_YYYY: 'DD/MM/YYYY',
  HH_MM_SS: 'hh:mm:ss',
  YYYY_MM_DD: 'YYYY-MM-DD',
  D_MMM_YYYY: 'D MMM YYYY',
  DDD_DD_MMM: 'ddd, DD MMM',
  YYYY_MM_DD__HH_MM_SS: 'YYYY_MM_DD__HH_mm_ss',
  TIME_STAMP: 'YYYY-MM-DDTHH:mm:ssZ',
  HH_MM_SSZ: 'HH:mm:ssZ',
  YYYY_MM_DD2: 'YYYY-MM-DD',
  MMM_YYYY: 'MMMM YYYY'
};

const getStartAndEndOfMonth = (
  inputDate: string | Date
): { startDate: string; endDate: string } => {
  const date = dayjs(inputDate);

  return {
    startDate: date.startOf('month').format(dateFormat.YYYY_MM_DD),
    endDate: date.endOf('month').format(dateFormat.YYYY_MM_DD)
  };
};

const getDayName = (dateStr: string, t: (key: string) => string): string => {
  const daysKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dayIndex = dayjs(dateStr).day();
  const dayKey = daysKeys[dayIndex];
  return t(`days.${dayKey}`);
};

const getAllDatesOfMonth = (inputDate: string): DateInfo[] => {
  const date = dayjs(inputDate);
  const daysInMonth = date.daysInMonth();

  return Array.from({ length: daysInMonth }, (_, i) => {
    const currentDate = date.date(i + 1);
    return {
      fullDate: currentDate.format(dateFormat.YYYY_MM_DD),
      day: currentDate.date()
    };
  });
};

const getDayFromDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.getDate();
};

const getTimestamps = (range: string) => {
  const now = moment();
  const end = now.format(dateFormat.TIME_STAMP);
  let start = '';

  if (range === TIME_RANGE.TODAY) {
    start = now.clone().startOf('day').format(dateFormat.TIME_STAMP);
  } else if (range === TIME_RANGE.WEEKLY) {
    start = now.clone().subtract(7, 'days').format(dateFormat.TIME_STAMP);
  } else if (range === TIME_RANGE.MONTHLY) {
    start = now.clone().subtract(1, 'month').format(dateFormat.TIME_STAMP);
  } else if (range === TIME_RANGE.YEARLY) {
    start = now.clone().subtract(1, 'year').format(dateFormat.TIME_STAMP);
  }

  return { startTimestamp: start, endTimestamp: end };
};

interface MenuItemType {
  label: string;
  value: string | number;
}
const getPastYears = (count: number): MenuItemType[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => {
    const year = currentYear - i;
    return {
      label: year.toString(),
      value: year.toString()
    };
  });
};

const toFullTime = (time: string) => `${time}:00+00`;

export {
  getAbbreviatedMonth,
  convertToAmPm,
  dateFormat,
  getStartAndEndOfMonth,
  getAllDatesOfMonth,
  getDayName,
  getDayFromDate,
  getTimestamps,
  getPastYears,
  toFullTime,
  getFormattedTime
};
